import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addDays, startOfDay } from 'date-fns';
import { normalize, schema } from 'normalizr';
import { combineReducers } from 'redux';
import { createTableSlice } from '../../../common/datagrid';
import {
    AgreementDto,
    AgreementPriceDto,
    AgreementPricesDto,
    AgreementStatus,
    CreateAgreementDto,
    ExternalPriceDto,
    PriceDto,
    ReservedPrice,
    SaveAgreementPriceDto,
    SaveTagsDto,
    TableType,
    TagDto,
    UpdateAgreementDto
} from '../../../common/PricePrgClient';
import { createAsyncAction, toDateString } from '../../../utils';
import { fetchWarehouses } from '../../stock/redux/stockSlices';

const namespaces = {
    agreements: 'agreements',
    agreementUi: 'agreementUi',
    agreementRefExtPrices: 'agreementRefExtPrices',
    agreementRefPrices: 'agreementRefPrices',
    agreementPrices: 'agreementPrices',
    agreementPriceSavings: 'agreementPriceSavings',
    reservedAgreementPrices: 'reservedAgreementPrices',
    agreementPriceTable: 'agreementPriceTable' as const,
    agreementTable: 'agreementTable' as const,
    tags: 'agreementTags'
};

const agreementEntity = new schema.Entity<AgreementDto>('agreements', undefined, {
    idAttribute: 'agreementId'
});

const agreementPriceEntity = new schema.Entity<AgreementPriceDto>('agreementPrices', undefined, {
    idAttribute: 'agreementPriceId'
});

const externalPriceEntity = new schema.Entity<ExternalPriceDto>('externalPrices', undefined, {
    idAttribute: 'externalPriceId'
});

const priceEntity = new schema.Entity<PriceDto>('prices', undefined, {
    idAttribute: 'priceId'
});

type CopyAgreementRequest = {
    agreementId: number;
    additionalDetails: CreateAgreementDto;
    awareOfPriceConflicts?: boolean;
};

export const fetchAgreements = createAsyncAction<void, AgreementDto[]>(
    namespaces.agreements,
    'fetchAgreements'
);
export const updateAgreement = createAsyncAction<UpdateAgreementDto, AgreementDto>(
    namespaces.agreements,
    'updateAgreement'
);
export const createAgreement = createAsyncAction<CreateAgreementDto, AgreementDto>(
    namespaces.agreements,
    'createAgreement'
);
export const copyAgreement = createAsyncAction<CopyAgreementRequest, AgreementDto>(
    namespaces.agreements,
    'copyAgreement'
);
export const deleteAgreement = createAsyncAction<number, number>(namespaces.agreements, 'deleteAgreement');
const agreementsSlice = createSlice({
    name: namespaces.agreements,
    initialState: {} as { [agreementId: number]: AgreementDto },
    reducers: {
        agreementUpdated: (state, action: PayloadAction<AgreementDto>) => {
            state[action.payload.agreementId] = action.payload;
        }
    },
    extraReducers: builder =>
        builder
            .addCase(fetchAgreements.success, (_, action) => {
                const normalized = normalize(action.payload, [agreementEntity]);
                return normalized.entities.agreements ?? {};
            })
            .addCase(updateAgreement.success, (state, action) => {
                const { payload } = action;
                const updatedAgreement = state[payload.agreementId];
                if (updatedAgreement != null) {
                    updatedAgreement.name = payload.name;
                    updatedAgreement.description = payload.description;
                    updatedAgreement.agreementStatus = payload.agreementStatus;
                    updatedAgreement.handleExternalPrices = payload.handleExternalPrices;
                    updatedAgreement.permittedUserGroups = payload.permittedUserGroups;
                    updatedAgreement.publishedDate = payload.publishedDate;
                }
            })
            .addCase(createAgreement.success, (state, action) => {
                const { payload } = action;
                state[payload.agreementId] = payload;
            })
            .addCase(copyAgreement.success, (state, action) => {
                const { payload } = action;
                state[payload.agreementId] = payload;
            })
            .addCase(deleteAgreement.success, (state, action) => {
                delete state[action.payload];
            })
            .addCase(updateAgreementTags.success, (state, action) => {
                state[action.payload.agreementId].tags = action.payload.tags;
            })
});
export const { agreementUpdated } = agreementsSlice.actions;

type AgreementUI = {
    agreementId: number | null;
    refPricelistId: number | null;
    refExtPricelistId: number | null;
    pricelistDate: string;
    warehouseId: number | null;
    stockDate: string;
    isSaving: boolean;
    isShowingCreateDialog: boolean;
    isShowingCopyDialog: boolean;
    copyPriceConflicts: AgreementPriceDto[];
    showAllArticles: boolean;
    statusFilter: AgreementStatus | null;
    isExporting: boolean;
};

const agreementUiSlice = createSlice({
    name: namespaces.agreementUi,
    initialState: {
        agreementId: null,
        refPricelistId: null,
        refExtPricelistId: null,
        pricelistDate: toDateString(addDays(startOfDay(new Date()), 1)),
        stockDate: toDateString(addDays(startOfDay(new Date()), 1)),
        warehouseId: null,
        isSaving: false,
        isShowingCreateDialog: false,
        isShowingCopyDialog: false,
        copyPriceConflicts: [],
        showAllArticles: true,
        statusFilter: null,
        isExporting: false
    } as AgreementUI,
    reducers: {
        agreementSelected: (state, action: PayloadAction<number>) => {
            state.agreementId = action.payload;
            state.refPricelistId = null;
            state.refExtPricelistId = null;
        },
        refPricelistIdSelected: (state, action: PayloadAction<number>) => {
            state.refPricelistId = action.payload;
        },
        refExtPricelistIdSelected: (state, action: PayloadAction<number>) => {
            state.refExtPricelistId = action.payload;
        },
        pricelistDateSelected: (state, action: PayloadAction<string>) => {
            state.pricelistDate = action.payload;
        },
        agreementWarehouseSelected: (state, action: PayloadAction<number>) => {
            state.warehouseId = action.payload;
        },
        agreementStockDateSelected: (state, action: PayloadAction<string>) => {
            state.stockDate = action.payload;
        },
        showCreateDialog: (state, action: PayloadAction<boolean>) => {
            state.isShowingCreateDialog = action.payload;
            state.isSaving = false;
        },
        showCopyDialog: (state, action: PayloadAction<boolean>) => {
            state.isShowingCopyDialog = action.payload;

            if (!action.payload) {
                state.copyPriceConflicts = [];
            }
        },
        copyAgreementRequiresConfirmation: (state, action: PayloadAction<AgreementPriceDto[]>) => {
            state.copyPriceConflicts = action.payload;
            state.isSaving = false;
        },
        cancelCopyAgreementRequiresConfirmation: state => {
            state.copyPriceConflicts = [];
        },
        showAllArticlesChanged: (state, action: PayloadAction<boolean>) => {
            state.showAllArticles = action.payload;
        },
        setAgreementStatusFilter: (state, action: PayloadAction<AgreementStatus | null>) => {
            state.statusFilter = action.payload;
        },
        setIsExporting: (state, action: PayloadAction<boolean>) => {
            state.isExporting = action.payload;
        }
    },
    extraReducers: builder =>
        builder
            .addCase(fetchWarehouses.success, (state, { payload }) => {
                if (payload.length === 0) {
                    return;
                }

                state.warehouseId =
                    payload.find(w => w.isPrimaryWarehouse)?.warehouseId ?? payload[0]?.warehouseId ?? state;
            })
            .addCase(createAgreement.request, state => {
                state.isSaving = true;
            })
            .addCase(copyAgreement.request, state => {
                state.isSaving = true;
            })
            .addCase(createAgreement.success, state => {
                state.isSaving = false;
                state.isShowingCreateDialog = false;
            })
            .addCase(deleteAgreement.success, (state, action) => {
                if (action.payload === state.agreementId) {
                    state.agreementId = null;
                }
            })
            .addCase(copyAgreement.success, state => {
                state.isShowingCopyDialog = false;
                state.copyPriceConflicts = [];
                state.isSaving = false;
            })
});

export const {
    agreementSelected,
    refPricelistIdSelected,
    refExtPricelistIdSelected,
    pricelistDateSelected,
    agreementWarehouseSelected,
    agreementStockDateSelected,
    showCreateDialog,
    showCopyDialog,
    copyAgreementRequiresConfirmation,
    cancelCopyAgreementRequiresConfirmation,
    showAllArticlesChanged,
    setAgreementStatusFilter,
    setIsExporting
} = agreementUiSlice.actions;

type FetchExternalPricesRequest = { pricelistId: number; pricelistDate: string };
export const fetchExternalPrices = createAsyncAction<FetchExternalPricesRequest, ExternalPriceDto[]>(
    namespaces.agreementRefExtPrices,
    'fetchExternalPrices'
);

const refExtPrices = createSlice({
    name: namespaces.agreementRefExtPrices,
    initialState: {} as { [externalPriceId: number]: ExternalPriceDto },
    reducers: {},
    extraReducers: builder =>
        builder.addCase(fetchExternalPrices.success, (state, action) => {
            const normalized = normalize(action.payload, [externalPriceEntity]);
            return normalized.entities.externalPrices ?? {};
        })
});

type FetchPricesRequest = FetchExternalPricesRequest;
export const fetchPrices = createAsyncAction<FetchPricesRequest, PriceDto[]>(
    namespaces.agreementRefPrices,
    'fetchPrices'
);

const refPrices = createSlice({
    name: namespaces.agreementRefPrices,
    initialState: {} as { [priceId: number]: PriceDto },
    reducers: {},
    extraReducers: builder =>
        builder.addCase(fetchPrices.success, (state, action) => {
            const normalized = normalize(action.payload, [priceEntity]);
            return normalized.entities.prices ?? {};
        })
});

type SaveAgreementPriceType = 'price' | 'extPrice' | 'discount' | 'comment' | 'isActive';

type SaveAgreementPriceRequest = SaveAgreementPriceDto & { type: SaveAgreementPriceType };
type SaveAgreementPriceSuccess = AgreementPriceDto & Partial<{ type: SaveAgreementPriceType }>;

export const fetchAgreementPrices = createAsyncAction<{ agreementId: number }, AgreementPricesDto>(
    namespaces.agreementPrices,
    'fetchAgreementPrices'
);
export const saveAgreementPrice = createAsyncAction<
    SaveAgreementPriceRequest,
    SaveAgreementPriceSuccess,
    SaveAgreementPriceRequest
>(namespaces.agreementPrices, 'saveAgreementPrice');

export const saveAgreementPriceDiscountRequest = createAction<{
    agreementPriceId: number;
    discount: number | null;
    type: 'in' | 'ext';
}>(`@${namespaces.agreementPrices}/saveAgreementPriceDiscountRequest`);

const agreementPricesSlice = createSlice({
    name: namespaces.agreementPrices,
    initialState: {} as { [agreementPriceId: number]: AgreementPriceDto },
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchAgreementPrices.request, () => ({}))
            .addCase(fetchAgreementPrices.success, (_, action) => {
                const normalized = normalize(action.payload.agreementPrices, [agreementPriceEntity]);
                return normalized.entities.agreementPrices ?? {};
            })
            .addCase(saveAgreementPrice.success, (state, action) => {
                const { payload } = action;
                if (payload.price === 0) {
                    delete state[action.payload.agreementPriceId];
                } else {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { type, ...rest } = action.payload;
                    state[action.payload.agreementPriceId] = rest;
                }
            })
});

const initialTableColumns = [
    'isInactive',
    'productId',
    'productName',
    'country',
    'stock',
    'price',
    'comment',
    'extPrice',
    'refPrice',
    'refExtPrice'
].map(key => ({
    key: key,
    hide: true,
    width: undefined
}));

export const updateAgreementPriceActiveStatus = createAction<{ agreementPriceId: number; active: boolean }>(
    '@agreementPrice/updateAgreementPriceActiveStatus'
);

const agreementPriceTableSlice = createTableSlice(namespaces.agreementPriceTable, {
    table: TableType.AgreementPrice,
    filters: {
        isInactive: '',
        productId: '',
        productName: '',
        country: '',
        stock: '',
        price: '',
        comment: '',
        extPrice: '',
        refPrice: '',
        refExtPrice: ''
    },
    showTooltips: true,
    sorting: {
        column: 'createdDate',
        direction: 'desc'
    },
    columns: initialTableColumns
});
export const { agreementPriceTableActions } = agreementPriceTableSlice;

type PriceSavings = {
    price?: number;
    extPrice?: number;
    comment?: string;
};

const agreementPriceSavingsSlice = createSlice({
    name: namespaces.agreementPriceSavings,
    initialState: {} as { [productId: number]: PriceSavings },
    reducers: {},
    extraReducers: builder => {
        const removePriceSaving = (
            state: { [productId: number]: PriceSavings },
            productId: number,
            type?: SaveAgreementPriceType
        ) => {
            const priceSavings = state[productId];
            if (priceSavings == null) {
                return;
            }

            switch (type) {
                case 'price':
                    priceSavings.price = undefined;
                    break;
                case 'extPrice':
                    priceSavings.extPrice = undefined;
                    break;
                case 'comment':
                    priceSavings.comment = undefined;
                    break;

                case null:
                case undefined:
                    delete state[productId];
                    break;

                default:
                    break;
            }

            if (
                priceSavings.extPrice == null &&
                priceSavings.price === null &&
                priceSavings.comment == null
            ) {
                delete state[productId];
            }
        };

        return builder
            .addCase(saveAgreementPrice.request, (state, { payload }) => {
                let priceSavings = state[payload.productId];
                if (priceSavings == null) {
                    priceSavings = {};
                    state[payload.productId] = priceSavings;
                }

                switch (payload.type) {
                    case 'price':
                        priceSavings.price = payload.price;
                        break;
                    case 'extPrice':
                        priceSavings.extPrice = payload.externalPrice;
                        break;
                    case 'comment':
                        priceSavings.comment = payload.comment;
                        break;

                    default:
                        break;
                }
            })
            .addCase(saveAgreementPrice.success, (state, { payload }) => {
                removePriceSaving(state, payload.productId, payload.type);
            })
            .addCase(saveAgreementPrice.failure, (state, { payload }) => {
                removePriceSaving(state, payload.productId, payload.type);
            });
    }
});

type FetchReservedPriceRequest = { productId: number; agreementId: number };

export const getAgreementTags = createAsyncAction<number, TagDto[]>(namespaces.tags, 'getAgreementTags');
export const updateAgreementTags = createAsyncAction<SaveTagsDto, { agreementId: number; tags: TagDto[] }>(
    namespaces.tags,
    'updateAgreementTags'
);

export const fetchReservedPrice = createAsyncAction<FetchReservedPriceRequest, ReservedPrice>(
    namespaces.reservedAgreementPrices,
    'fetchReservedPrice'
);

const reservedAgreementPricesSlice = createSlice({
    name: namespaces.reservedAgreementPrices,
    initialState: {} as { [productId: number]: number[] },
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchReservedPrice.success, (state, action) => {
                const { payload } = action;
                if (payload.agreementPriceIds.length === 0) {
                    delete state[action.payload.productId];
                } else {
                    state[action.payload.productId] = action.payload.agreementPriceIds;
                }
            })
            .addCase(fetchAgreementPrices.success, (_, action) => {
                const result: { [productId: number]: number[] } = {};

                for (const rp of action.payload.reservedPrices) {
                    result[rp.productId] = rp.agreementPriceIds;
                }

                return result;
            })
});

const initialAgreementTableColumns = ['name', 'validAt', 'createdBy'].map(key => ({
    key: key,
    hide: false,
    width: undefined
}));

const agreementTableSlice = createTableSlice('agreementTable', {
    table: TableType.Agreement,
    filters: {
        name: '',
        validAt: '',
        createdBy: ''
    },
    showTooltips: true,
    columns: initialAgreementTableColumns,
    sorting: {
        column: 'publishedDate',
        direction: 'desc'
    }
});
export const { agreementTableActions } = agreementTableSlice;

export default combineReducers({
    agreements: agreementsSlice.reducer,
    agreementPrices: agreementPricesSlice.reducer,
    agreementPriceTable: agreementPriceTableSlice.reducer,
    reservedAgreementPrices: reservedAgreementPricesSlice.reducer,
    refExtPrices: refExtPrices.reducer,
    refPrices: refPrices.reducer,
    pricesToBeSaved: agreementPriceSavingsSlice.reducer,
    ui: agreementUiSlice.reducer,
    agreementTable: agreementTableSlice.reducer
});
